import { HttpClient } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { BehaviorSubject, merge, mergeMap, Observable, switchMap } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { BlogFeedItem, GqlPost, WPPostsResponse } from './types';
import { SectionHeaderComponent } from '../section-header/section-header.component';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { OnVisibleDirective } from '../../directives/on-visible/on-visible.directive';

const BLOG_POST_QUERY = `
query GetBlogPosts($tagIds: [ID!]) {
  posts(first: 3, after: "", where: { tagIn: $tagIds }) {
  nodes {
    date
    link
    title
    excerpt
    author {
      node {
        avatar {
          url
        }
        name
      }
    }
    categories {
      edges {
        node {
          name
          link
        }
      }
    }
    featuredImage {
      node {
        mediaDetails {
          sizes {
            name
            sourceUrl
          }
        }
      }
    }
  }
 }
}`;

@Component({
    selector: 'kb-blog-feed',
    templateUrl: './blog-feed.component.html',
    styleUrls: ['./blog-feed.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        OnVisibleDirective,
        NgIf,
        SectionHeaderComponent,
        NgFor,
        AsyncPipe,
    ],
})
export class BlogFeedComponent implements OnInit, OnChanges {
    @Input() title: string;
    @Input() tagIds: string[] | undefined;
    @Input() showRelatedTags = false;
    @Input() includeCategoryLinks = true;
    topPosts$: Observable<BlogFeedItem[]>;
    private isInView$ = new BehaviorSubject<boolean>(false);
    private tagIds$ = new BehaviorSubject<string[]>([]);

    constructor(
        private httpClient: HttpClient,
        private domSanitizer: DomSanitizer,
        private changeDetectorRef: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        const fetchPosts = (tagIds?: string[]) =>
            this.httpClient
                .post<WPPostsResponse>(
                    `https://www.artsupplies.co.uk/blog/graphql`,
                    JSON.stringify({
                        query: BLOG_POST_QUERY,
                        variables: { tagIds },
                    }),
                    {
                        headers: {
                            'Content-type': 'application/json',
                        },
                    },
                )
                .pipe(map((data) => data.data.posts.nodes.map((post) => this.toBlogFeedItem(post))));

        if (this.showRelatedTags) {
            const clear$ = this.tagIds$.pipe(map(() => []));

            const results$ = this.tagIds$.pipe(
                switchMap(
                    () =>
                        this.isInView$.pipe(
                            filter((val) => val === true),
                            take(1),
                        ),
                    (outerValue) => outerValue,
                ),
                filter((ids) => (ids ? 0 < ids.length : true)),
                mergeMap(fetchPosts),
            );

            this.topPosts$ = merge(clear$, results$);
        } else {
            this.topPosts$ = this.isInView$.pipe(
                filter((val) => val === true),
                take(1),
                mergeMap(() => fetchPosts()),
            );
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('tagIds' in changes) {
            this.tagIds$.next(this.tagIds ?? []);
        }
    }

    onVisibilityChange(isVisible: boolean) {
        this.isInView$.next(isVisible);
    }

    private toBlogFeedItem(post: GqlPost): BlogFeedItem {
        return {
            date: post.date,
            link: post.link,
            author: post.author.node.name,
            authorAvatarUrl: post.author.node.avatar.url,
            title: this.domSanitizer.bypassSecurityTrustHtml(post.title),
            text: this.domSanitizer.bypassSecurityTrustHtml(post.excerpt),
            imageUrl:
                post.featuredImage?.node.mediaDetails.sizes.find((s) => s.name === 'post-thumbnail' || s.name === 'medium_large' || s.name === 'medium')
                    ?.sourceUrl ?? '',
            categories: post.categories.edges?.map((c) => c.node) ?? [],
        };
    }
}
